import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import constant from "../../constant";
import OnboardPatientForm from "../ui/onboardPatientForm";

const EditOnboardPatient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hospitalId, setHospitalId] = useState(localStorage.getItem('hospitalId'));

  useEffect(() => {
    const fetchPatientAndData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      try {
        const patientResponse = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const patientResult = await patientResponse.json();

        if (patientResponse.ok) {
          setInitialValues({
            patient_id: patientResult.patient_id,
            patient_hospital_id: patientResult.patient_hospital_id,
            bed_number: patientResult.bed_number,
            ward_name: patientResult.ward_name,
            name: patientResult.name,
            // date_of_birth: patientResult.date_of_birth,
            age: patientResult.age,
            gender: patientResult.gender,
            admitted_on: patientResult.admitted_on,
            hospital_id: patientResult.hospital_id,
            is_released: patientResult.is_released,
            is_active: patientResult.is_active,
            device_id: patientResult.device?.device_id || '',
            doctor_id: patientResult.doctor?.user_id || '',
            // caretaker_name: patientResult.caretaker.name,
            // caretaker_email: patientResult.caretaker.email,
            // caretaker_mobile: patientResult.caretaker.mobile,
          });
        } else {
          setError(patientResult.msg || 'Failed to fetch patient data');
        }

        const doctorsResponse = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/doctor/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const doctorsResult = await doctorsResponse.json();
        if (doctorsResponse.ok) {
          setDoctors(doctorsResult || []);
        } else {
          setError(doctorsResult.msg || 'Failed to fetch doctors');
        }

        const devicesResponse = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/devices?type=unassigned`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const devicesResult = await devicesResponse.json();
        if (devicesResponse.ok) {

          // setDevices(devicesResult || []);

          const updatedDevices = devicesResult ? [...devicesResult] : [];

          if (patientResult?.device) {
            updatedDevices.push(patientResult.device);
          }

          setDevices(updatedDevices)

        } else {
          setError(devicesResult.msg || 'Failed to fetch devices');
        }
      } catch (error) {
        setError(error.message || 'Error fetching data');
      } finally {
        setLoading(false);
      }
    };

    fetchPatientAndData();
  }, [id, hospitalId]);

  const handleCancel = () => {
    navigate(`/admin/patients/${hospitalId}`);
  };

  const handleClearError = () => {
    setError(null);
  };

  const handleSubmit = async (patientData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiData = {
        patient_hospital_id: patientData.patient_hospital_id,
        bed_number: patientData.bed_number,
        ward_name: patientData.ward_name,
        name: patientData.name,
        // date_of_birth: patientData.date_of_birth,
        age: patientData.age,
        gender: patientData.gender,
        admitted_on: patientData.admitted_on,
        hospital_id: hospitalId,
        is_released: patientData.is_released,
        is_active: patientData.is_active,
        device_id: patientData.device_id,
        doctor_id: patientData.doctor_id,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/patient/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();
      if (response.ok) {
        navigate(`/admin/patients/${hospitalId}`);
      } else {
        throw new Error(result.msg || 'Failed to update patient');
      }
    } catch (error) {
      setError(error.message || 'Error updating patient');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Edit Patient</h2>
          </div>
        </div>

        {initialValues && (
          <OnboardPatientForm
            initialValues={initialValues}
            doctors={doctors}
            devices={devices}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditMode={true}
            error={error}
            onClearError={handleClearError}
          />
        )}
      </div>
    </div>
  );
};

export default EditOnboardPatient;